/**
 * slider-lite.js — zero-dependency replacement for Slick carousel.
 * Supports: slidesToShow (responsive), arrows, dots, infinite loop, autoplay.
 * Init: <div class="sx-slider" data-slides="3" data-slides-mobile="1.2" data-arrows="true" data-dots="true"></div>
 * Or imperatively: new SXSlider(el, options).
 */
class SXSlider {
  constructor(root, options = {}) {
    if (!root || root.dataset.sxInit === '1') return;
    root.dataset.sxInit = '1';
    this.root = root;
    this.options = Object.assign(
      {
        slidesToShow: parseFloat(root.dataset.slides) || 3,
        slidesToShowMobile: parseFloat(root.dataset.slidesMobile) || 1.2,
        mobileBreakpoint: 749,
        arrows: root.dataset.arrows !== 'false',
        dots: root.dataset.dots === 'true',
        infinite: root.dataset.infinite === 'true',
        autoplay: root.dataset.autoplay === 'true',
        autoplaySpeed: parseInt(root.dataset.autoplaySpeed, 10) || 4000,
        prevArrowLabel: root.dataset.prevLabel || 'Previous slide',
        nextArrowLabel: root.dataset.nextLabel || 'Next slide',
        prevArrowSrc: root.dataset.prevArrowSrc || '',
        nextArrowSrc: root.dataset.nextArrowSrc || '',
      },
      options
    );
    this.children = Array.from(root.children);
    if (this.children.length === 0) return;
    this.index = 0;
    this.build();
    this.bind();
    this.update();
    if (this.options.autoplay) this.startAutoplay();
  }

  get slidesPerView() {
    return window.innerWidth <= this.options.mobileBreakpoint
      ? this.options.slidesToShowMobile
      : this.options.slidesToShow;
  }

  get maxIndex() {
    return Math.max(0, this.children.length - Math.floor(this.slidesPerView));
  }

  build() {
    this.root.classList.add('sx-slider');
    const track = document.createElement('div');
    track.className = 'sx-slider__track';
    track.setAttribute('role', 'group');
    track.setAttribute('aria-roledescription', 'carousel');
    this.children.forEach((child, i) => {
      child.classList.add('sx-slider__slide');
      child.setAttribute('role', 'group');
      child.setAttribute('aria-roledescription', 'slide');
      child.setAttribute('aria-label', `${i + 1} of ${this.children.length}`);
      track.appendChild(child);
    });
    this.root.appendChild(track);
    this.track = track;

    if (this.options.arrows && this.children.length > 1) {
      const makeArrow = (dir, label, src) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `sx-slider__arrow sx-slider__arrow--${dir}`;
        btn.setAttribute('aria-label', label);
        if (src) {
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          btn.appendChild(img);
        } else {
          btn.innerHTML =
            dir === 'prev'
              ? '<svg width="12" height="20" viewBox="0 0 12 20" aria-hidden="true"><path d="M10 2L2 10l8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
              : '<svg width="12" height="20" viewBox="0 0 12 20" aria-hidden="true"><path d="M2 2l8 8-8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        }
        return btn;
      };
      this.prevBtn = makeArrow('prev', this.options.prevArrowLabel, this.options.prevArrowSrc);
      this.nextBtn = makeArrow('next', this.options.nextArrowLabel, this.options.nextArrowSrc);
      this.root.appendChild(this.prevBtn);
      this.root.appendChild(this.nextBtn);
    }

    if (this.options.dots && this.children.length > 1) {
      const dots = document.createElement('ul');
      dots.className = 'sx-slider__dots';
      dots.setAttribute('role', 'tablist');
      this.children.forEach((_, i) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        btn.setAttribute('role', 'tab');
        btn.dataset.index = String(i);
        li.appendChild(btn);
        dots.appendChild(li);
      });
      this.root.appendChild(dots);
      this.dots = dots;
    }
  }

  bind() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
    if (this.dots)
      this.dots.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-index]');
        if (btn) this.go(parseInt(btn.dataset.index, 10));
      });
    this._onResize = () => this.update();
    window.addEventListener('resize', this._onResize, { passive: true });

    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.go(this.index - 1);
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        this.go(this.index + 1);
        e.preventDefault();
      }
    });

    let startX = 0;
    let dx = 0;
    this.track.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        dx = 0;
      },
      { passive: true }
    );
    this.track.addEventListener(
      'touchmove',
      (e) => {
        dx = e.touches[0].clientX - startX;
      },
      { passive: true }
    );
    this.track.addEventListener('touchend', () => {
      if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
    });
  }

  go(i) {
    const max = this.maxIndex;
    if (this.options.infinite) {
      if (i < 0) i = max;
      if (i > max) i = 0;
    } else {
      i = Math.max(0, Math.min(max, i));
    }
    this.index = i;
    this.update();
  }

  update() {
    const per = this.slidesPerView;
    const slideWidth = 100 / per;
    this.children.forEach((c) => {
      c.style.flex = `0 0 ${slideWidth}%`;
      c.style.maxWidth = `${slideWidth}%`;
    });
    const offset = this.index * slideWidth;
    this.track.style.transform = `translate3d(-${offset}%, 0, 0)`;
    if (this.dots) {
      Array.from(this.dots.querySelectorAll('button')).forEach((b, i) => {
        const isActive = i === this.index;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
    }
    if (this.prevBtn) this.prevBtn.disabled = !this.options.infinite && this.index === 0;
    if (this.nextBtn) this.nextBtn.disabled = !this.options.infinite && this.index >= this.maxIndex;
  }

  startAutoplay() {
    this.stopAutoplay();
    this._timer = setInterval(() => this.go(this.index + 1), this.options.autoplaySpeed);
    this.root.addEventListener('mouseenter', () => this.stopAutoplay());
    this.root.addEventListener('mouseleave', () => this.startAutoplay());
  }

  stopAutoplay() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  destroy() {
    this.stopAutoplay();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }
}

(function autoInit() {
  const init = () => {
    document.querySelectorAll('[data-sx-slider]').forEach((el) => {
      if (el.dataset.sxInit !== '1') new SXSlider(el);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

window.SXSlider = SXSlider;
